import { holdComposingEnter } from './composition';
// A cloud zone's parts of the control plane (doc/20261018-cloud-zone-browser.md §3.2, cards 22–24).
import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined } from '@ant-design/icons';
import { Alert, Badge, Button, Card, Checkbox, ConfigProvider, Form, Input, Modal, Progress, Radio, Space, Tag, Typography } from 'antd';
import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trans } from '../i18n/Trans';
import { DEFAULT_DISPLAY_LANGUAGE } from '../../../shared/locale/locale.mjs';
import { useFormat, type Formatters } from '../i18n/format';
import { seal } from '../theme';
import { ApiError, controlApi } from './api';
import type { CloudFacts, CloudOffer, CloudRegion, Zone } from './api';
import { cloudOf, cloudStage, isAwake, isLiveCloud, isRestoring, powerChip, regionKey, timeZoneName, timeZonePlace, WEEKDAYS, weekdayDate } from './cloud';
import { HANDLE_MAX } from './handle';

const { Paragraph, Text } = Typography;
/** About how long a wake takes (the clean-shutdown spec's staging run measured 18.5–22.4 s). */
const WAKE_SECONDS = 20;

type ConsoleT = TFunction<'console'>;

function clock(format: Formatters, value: number, timeZone?: string): string {
  return format.date(new Date(value), { hour: '2-digit', minute: '2-digit', ...(timeZone === undefined ? {} : { timeZone }) });
}

function ago(t: ConsoleT, format: Formatters, value: number, now = Date.now()): string {
  const minutes = Math.max(0, Math.round((now - value) / 60_000));
  if (minutes < 1) return t('cloud.justNow');
  if (minutes < 60) return format.relative(-minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (hours < 24) return format.relative(-hours, 'hour');
  return format.relative(-Math.round(hours / 24), 'day');
}

/** A region's name in the display language where the catalogue knows it, else the offer's name, else its id. */
export function regionLabel(t: ConsoleT, id: string, regions: readonly CloudRegion[] = []): string {
  const key = regionKey(id);
  return key !== null ? t(`cloud.region.${key}.name`) : regions.find(region => region.id === id)?.name ?? id;
}

function regionPlace(t: ConsoleT, region: CloudRegion): string {
  const key = regionKey(region.id);
  return key !== null ? t(`cloud.region.${key}.place`) : region.place;
}

/** Brass marks crossing into the zone (card 22's rule): Open zone and Open trusted zone. */
export function BrassButton(props: React.ComponentProps<typeof Button>) {
  return <ConfigProvider theme={{ token: { colorPrimary: seal.color, colorPrimaryHover: seal.colorHover, colorPrimaryActive: seal.color } }}>
    <Button type="primary" {...props} />
  </ConfigProvider>;
}

/** Awake opens the zone; any other state opens the wait page, which wakes it first (§3.2.3). */
export function openZoneHref(cloud: CloudFacts, awake: boolean): string {
  // eslint-disable-next-line i18next/no-literal-string -- addresses and a route
  return awake ? `https://${cloud.webName}` : `/wake?tenant=${encodeURIComponent(cloud.tenantName)}&auto=1`;
}

export function OpenZoneButton({ zone, label }: { readonly zone: Zone; readonly label?: string }) {
  const { t } = useTranslation('console');
  const cloud = cloudOf(zone);
  if (!isLiveCloud(cloud)) return null;
  return <BrassButton href={openZoneHref(cloud, isAwake(zone))} target="_blank" rel="noreferrer">{label ?? t('cloud.openZone')}</BrassButton>;
}

export function CloudChip({ zone }: { readonly zone: Zone }) {
  const { t } = useTranslation('console');
  const chip = powerChip(zone);
  // eslint-disable-next-line i18next/no-literal-string -- antd colour and status names
  const color = { sealed: 'success', neutral: undefined, primary: 'processing', error: 'error' }[chip.tone];
  // eslint-disable-next-line i18next/no-literal-string -- antd status names
  const badge = ({ sealed: 'success', neutral: 'default', primary: 'processing', error: 'error' } as const)[chip.tone];
  return <Tag color={color}><Badge status={badge} /> {t(`cloud.power.${chip.label}`)}</Tag>;
}

/** The card's storage line (storage budget §5.1, §7), or null before any report. */
export function storageLine(t: ConsoleT, format: Formatters, zone: Zone): string | null {
  const storage = zone.storage;
  if (storage === null || storage === undefined) return null;
  if (storage.state === 'unknown') return t('cloud.storage.unknown');
  if (storage.state === 'over') return t('cloud.storage.over');
  const share = Math.round(100 * storage.usedBytes / Math.max(1, storage.budgetBytes));
  const gigabytes = Math.max(0, storage.budgetBytes) / 1024 ** 3;
  return t('cloud.storage.share', {
    share: format.number(share / 100, { style: 'percent' }),
    size: format.number(gigabytes, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
  });
}

/** The zone card's sentence and its short time (card 23). */
export function cloudCardNote(t: ConsoleT, format: Formatters, zone: Zone, now = Date.now()): { readonly note: string; readonly when: string | null } {
  const cloud = cloudOf(zone);
  if (cloud === null) return { note: t('cloud.note.starting'), when: null };
  const used = cloud.lastUsedAt === null ? null : t('cloud.card.used', { ago: ago(t, format, cloud.lastUsedAt, now) });
  const since = cloud.powerAt === null ? null : t('cloud.card.since', { time: clock(format, cloud.powerAt) });
  if (cloud.lifecycle === 'failed') return { note: t('cloud.note.failed'), when: null };
  if (cloud.lifecycle === 'provisioning') return { note: t('cloud.note.starting'), when: null };
  if (cloud.lifecycle === 'frozen') return { note: t('cloud.note.frozen'), when: null };
  if (isRestoring(zone)) return { note: t('cloud.note.restoring'), when: null };
  if (cloud.lifecycle === 'dormant' || cloud.power === 'asleep') {
    if (cloud.freezeBlocked === 'no_backup') return { note: t('cloud.note.freezeBlocked'), when: since };
    return { note: t('cloud.note.asleep', { seconds: WAKE_SECONDS }), when: since };
  }
  switch (cloud.power) {
    case 'awake': return { note: t('cloud.note.awake', { count: cloud.idleMinutes }), when: used };
    case 'quiescing': case 'releasing': {
      const idleOut = cloud.lastUsedAt !== null && now - cloud.lastUsedAt >= cloud.idleMinutes * 60_000;
      return { note: idleOut ? t('cloud.note.idleOut', { count: cloud.idleMinutes }) : t('cloud.note.goingToSleep'), when: used };
    }
    case 'waking': return { note: t('cloud.note.waking'), when: t('cloud.card.wakeWhen', { seconds: WAKE_SECONDS }) };
    default: return { note: t('cloud.note.starting'), when: null };
  }
}

/** Step 2's cloud fields (card 22): the owner, the web address under the tenant domain, the region. */
/**
 * The web address is the zone handle (readable intake addresses §3.1.3): checked by the caller as it is typed. Against a
 * Worker that predates handles the helper keeps its old copy.
 */
export function CloudDetailsFields({ offer, handles, example, handleError }: {
  readonly offer: Extract<CloudOffer, { enabled: true }>; readonly handles: boolean; readonly example: string;
  readonly handleError: (value: unknown) => string | null;
}) {
  const { t } = useTranslation('console');
  // eslint-disable-next-line i18next/no-literal-string -- an example intake address, the same in every language
  const address = `project.${example}@intake.lexside.ai`;
  return <>
    <Form.Item name="ownerEmail" label={t('deploy.ownerEmail.label')}
      extra={t('deploy.ownerEmail.help')}
      rules={[{ required: true, whitespace: true, type: 'email', message: t('deploy.ownerEmail.invalid', { example: 'name@firm.com' }) }]}>
      <Input placeholder="name@firm.com" maxLength={254} onKeyDown={holdComposingEnter} />
    </Form.Item>
    <Form.Item name="tenantName" label={t('cloud.details.addressLabel')}
      extra={handles
        ? <Trans t={t} i18nKey="cloud.details.addressHelpHandles" values={{ address }} components={{ code: <code /> }} />
        : t('cloud.details.addressHelp')}
      normalize={(value: string) => value.toLowerCase()}
      rules={[{ required: true, message: t('cloud.details.addressRequired') },
        { validator: async (_rule, value: unknown) => { const error = handleError(value); if (error !== null) throw new Error(error); } }]}>
      <Input className="cp-handle-input" addonBefore="https://" addonAfter={`.${offer.tenantDomain}`} maxLength={HANDLE_MAX} autoComplete="off" spellCheck={false} onKeyDown={holdComposingEnter} />
    </Form.Item>
    <Form.Item name="region" label={t('cloud.details.regionLabel')} extra={t('cloud.details.regionHelp')}
      rules={[{ required: true, message: t('cloud.details.regionRequired') }]}>
      <Radio.Group className="cp-region-options">
        {offer.regions.map(region => <Radio.Button key={region.id} value={region.id}>
          <span className="cp-region-name">{regionLabel(t, region.id, offer.regions)}</span> <span className="cp-region-place">{regionPlace(t, region)}</span>
        </Radio.Button>)}
      </Radio.Group>
    </Form.Item>
  </>;
}

function Stage({ state, label, time }: { readonly state: 'done' | 'active' | 'failed' | 'todo'; readonly label: string; readonly time?: string }) {
  const icon = state === 'done' ? <CheckCircleFilled className="cp-stage-done" /> : state === 'failed' ? <CloseCircleFilled className="cp-stage-failed" />
    : state === 'active' ? <LoadingOutlined className="cp-stage-active" /> : <span className="cp-stage-todo" />;
  return <li className={`cp-stage is-${state}`}>{icon}<span>{label}</span>{time !== undefined && <Text type="secondary" className="cp-stage-time">{time}</Text>}</li>;
}

// eslint-disable-next-line i18next/no-literal-string -- an Intl unit id
const seconds = (format: Formatters, ms: number) => format.number(Math.max(0, Math.round(ms / 1_000)), { style: 'unit', unit: 'second', unitDisplay: 'narrow' });

/** Step 3 for cloud (card 22): stages the control plane can see; connected; or failed with a bounded retry. */
export function CloudStart({ zone, createdAt, recreating, onTryAgain, onChooseDifferent }: {
  readonly zone: Zone; readonly createdAt: number; readonly recreating: boolean;
  readonly onTryAgain: () => void; readonly onChooseDifferent: () => void;
}) {
  const { t } = useTranslation('console');
  const format = useFormat();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 1_000); return () => window.clearInterval(timer); }, []);
  const cloud = cloudOf(zone);
  const stage = cloudStage(zone);
  const [waking, setWaking] = useState(false);
  const [wakeError, setWakeError] = useState<string | null>(null);
  const wakeAgain = async () => {
    setWaking(true); setWakeError(null);
    // The modal's poll picks up the zone waking.
    try { await controlApi.wakeZone(zone.id); } catch { setWakeError(t('common.unreachable')); } finally { setWaking(false); }
  };
  if (stage === 'connected' && cloud !== null) {
    return <div className="cp-cloud-connected">
      <Space align="center"><CheckCircleFilled className="cp-stage-done" /><Text strong>{t('cloud.start.connected')}</Text></Space>
      <Paragraph type="secondary">{t('cloud.start.connectedLine', { name: zone.name, count: Math.round((now - createdAt) / 1_000), address: `https://${cloud.webName}` })}</Paragraph>
      <BrassButton href={`https://${cloud.webName}`} target="_blank" rel="noreferrer">{t('cloud.openTrustedZone')}</BrassButton>
      <Paragraph type="secondary" className="cp-command-hint">{t('cloud.start.connectedHint', { count: cloud.idleMinutes })}</Paragraph>
    </div>;
  }
  const failed = stage === 'failed';
  // Released asleep by the China reachability check (part 2 §4 I3): a wake tries again, with new addresses if needed.
  const stopped = stage === 'stopped';
  return <div className="cp-cloud-start">
    {stopped && <Alert type="warning" showIcon message={t('cloudDetail.wakeNotReadyTitle')} description={t('cloudDetail.wakeNotReady')} />}
    {stopped && wakeError !== null && <Alert type="error" showIcon message={wakeError} />}
    {failed && <Alert type="error" showIcon message={cloud?.error === 'capacity' ? t('cloud.start.failedCapacity') : t('cloud.start.failedOther')}
      description={t('cloud.start.failedDetail')} />}
    {recreating && <Alert type="info" showIcon message={t('cloud.start.cleaningUp')} description={t('cloud.start.cleaningUpDetail')} />}
    <ol className="cp-stages">
      <Stage state="done" label={t('cloud.start.stageCreated')} time={seconds(format, 1_000)} />
      <Stage state={failed || stopped ? 'failed' : 'active'} label={failed || stopped ? t('cloud.start.stageStartingFailed') : t('cloud.start.stageStarting')} time={seconds(format, now - createdAt)} />
      <Stage state="todo" label={t('cloud.start.stageFirstConnection')} />
    </ol>
    {stopped ? <div className="cp-modal-footer">
      <Button type="primary" loading={waking} onClick={() => void wakeAgain()}>{t('cloud.powerPanel.wakeNow')}</Button>
    </div> : failed ? <div className="cp-modal-footer">
      <Button onClick={onChooseDifferent} disabled={recreating}>{t('cloud.start.chooseDifferent')}</Button>
      <Button type="primary" onClick={onTryAgain} loading={recreating}>{t('common.tryAgain')}</Button>
    </div> : <Paragraph type="secondary" className="cp-command-hint">{t('cloud.start.hint')}</Paragraph>}
  </div>;
}

/** The Power panel (card 24): Sleep now while awake, Wake now while asleep, progress while waking. */
export function PowerPanel({ zone, onChanged, onUnauthorized }: {
  readonly zone: Zone; readonly onChanged: () => void; readonly onUnauthorized: () => void;
}) {
  const { t } = useTranslation('console');
  const format = useFormat();
  const cloud = cloudOf(zone);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const waking = cloud?.power === 'waking';
  useEffect(() => {
    if (!waking) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [waking]);
  if (!isLiveCloud(cloud)) return null;
  const act = async (call: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await call(); setConfirming(false); onChanged(); }
    catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onUnauthorized();
      else if (caught instanceof ApiError && caught.code === 'zone_waking') setError(t('cloud.powerPanel.errorWaking'));
      else if (caught instanceof ApiError && caught.code === 'zone_not_active') setError(t('cloud.powerPanel.errorNotActive'));
      else setError(t('common.unreachable'));
    } finally { setBusy(false); }
  };
  const asleep = cloud.lifecycle === 'dormant' || cloud.lifecycle === 'frozen' || cloud.power === 'asleep';
  let body: React.ReactNode;
  if (waking && cloud.powerReason === 'restoring') {
    body = <div className="cp-power-progress">
      <Progress percent={100} showInfo={false} status="active" />
      <Text type="secondary">{t('cloud.note.restoring')}</Text>
    </div>;
  } else if (waking) {
    const elapsed = cloud.powerAt === null ? 0 : (now - cloud.powerAt) / 1_000;
    body = <div className="cp-power-progress">
      <Progress percent={Math.min(95, Math.round(elapsed / WAKE_SECONDS * 100))} showInfo={false} status="active" />
      <Text type="secondary">{t('cloud.powerPanel.waking', { seconds: WAKE_SECONDS })}</Text>
    </div>;
  } else if (asleep) {
    body = <div className="cp-power-row">
      <Paragraph>{cloud.lifecycle === 'frozen' ? t('cloud.powerPanel.frozen')
        : cloud.powerAt === null ? t('cloud.powerPanel.asleep', { seconds: WAKE_SECONDS })
          : t('cloud.powerPanel.asleepSince', { time: clock(format, cloud.powerAt), seconds: WAKE_SECONDS })}</Paragraph>
      <Button onClick={() => void act(() => controlApi.wakeZone(zone.id))} loading={busy}>{t('cloud.powerPanel.wakeNow')}</Button>
    </div>;
  } else if (cloud.power === 'awake') {
    body = <div className="cp-power-row">
      <Paragraph>{cloud.lastUsedAt === null ? t('cloud.powerPanel.awake', { count: cloud.idleMinutes, seconds: WAKE_SECONDS })
        : t('cloud.powerPanel.awakeUsed', { ago: ago(t, format, cloud.lastUsedAt, now), count: cloud.idleMinutes, seconds: WAKE_SECONDS })}</Paragraph>
      <Button onClick={() => setConfirming(true)}>{t('cloud.powerPanel.sleepNow')}</Button>
    </div>;
  } else {
    body = <Paragraph>{cloud.power === null ? t('cloud.powerPanel.starting') : t('cloud.powerPanel.goingToSleep')}</Paragraph>;
  }
  return <Card title={t('cloud.powerPanel.title')} className="cp-facts">
    {body}
    {error !== null && <Alert className="cp-inline-error" type="error" showIcon message={error} />}
    <Modal title={t('cloud.powerPanel.confirmTitle', { name: zone.name })} open={confirming} okText={t('cloud.powerPanel.sleepNow')} confirmLoading={busy}
      onOk={() => void act(() => controlApi.sleepZone(zone.id))} onCancel={() => setConfirming(false)}>
      <Paragraph>{t('cloud.powerPanel.confirmBody', { seconds: WAKE_SECONDS })}</Paragraph>
      <Paragraph type="secondary">{t('cloud.powerPanel.confirmNote')}</Paragraph>
    </Modal>
  </Card>;
}

function browserTimeZone(): string {
  // eslint-disable-next-line i18next/no-literal-string -- an IANA time zone id
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

/** The schedule's zone in words: "Hong Kong time" in English, the runtime's own name ("香港时间") otherwise. */
function timeZoneLabel(t: ConsoleT, format: Formatters, timeZone: string): string {
  const place = timeZonePlace(timeZone);
  if (place === null) return t('cloud.prewarm.utc');
  const name = format.language === DEFAULT_DISPLAY_LANGUAGE ? null : timeZoneName(timeZone, format.language);
  return name ?? t('cloud.prewarm.timeZone', { place });
}

/** Wake before work (card 24): days, a time and the browser's time zone; the server answers when it next wakes. */
export function WakeBeforeWork({ zone, onSaved, onUnauthorized }: {
  readonly zone: Zone; readonly onSaved: () => void; readonly onUnauthorized: () => void;
}) {
  const { t } = useTranslation('console');
  const format = useFormat();
  const cloud = cloudOf(zone);
  const stored = cloud?.prewarm?.schedule ?? null;
  const [days, setDays] = useState<number[]>(() => [...(stored?.days ?? [1, 2, 3, 4, 5])]);
  const [at, setAt] = useState(stored?.at ?? '08:30');
  const [saving, setSaving] = useState<'save' | 'off' | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setDays([...(stored?.days ?? [1, 2, 3, 4, 5])]); setAt(stored?.at ?? '08:30'); }, [stored?.days.join(','), stored?.at]);
  if (!isLiveCloud(cloud)) return null;
  // Shown in the schedule's own time zone; a save uses the browser's (§3.2.3), and says so when they differ.
  const browser = browserTimeZone();
  const timeZone = stored?.timeZone ?? browser;
  const save = async (schedule: { days: number[]; at: string; timeZone: string } | null) => {
    setSaving(schedule === null ? 'off' : 'save'); setError(null);
    try { await controlApi.setPrewarm(zone.id, schedule); onSaved(); }
    catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onUnauthorized();
      else if (caught instanceof ApiError && caught.code === 'invalid_schedule') setError(t('cloud.prewarm.errorSchedule'));
      else setError(t('common.unreachable'));
    } finally { setSaving(null); }
  };
  const next = cloud.prewarm?.next ?? null;
  const nextText = next === null ? null : format.date(new Date(next), { weekday: 'long', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone });
  const weekdays = WEEKDAYS.map(day => ({ value: day, label: format.date(weekdayDate(day), { weekday: 'short', timeZone: 'UTC' }) }));
  return <Card title={t('cloud.prewarm.title')} className="cp-facts">
    <Paragraph type="secondary">{t('cloud.prewarm.intro')}</Paragraph>
    {(cloud.lifecycle === 'dormant' || cloud.lifecycle === 'frozen') && stored !== null && <Alert className="cp-inline-error" type="info" showIcon message={t('cloud.prewarm.paused')}
      description={t('cloud.prewarm.pausedDetail')} />}
    <Space direction="vertical" size="middle" className="cp-prewarm">
      <div><Text className="cp-field-label">{t('cloud.prewarm.days')}</Text>
        <Checkbox.Group value={days} onChange={(value) => setDays(value as number[])}
          options={weekdays} /></div>
      <div><Text className="cp-field-label">{t('cloud.prewarm.at')}</Text>
        <Space><Input type="time" value={at} onChange={(event) => setAt(event.target.value)} style={{ width: 120 }} aria-label={t('cloud.prewarm.time')} />
          <Text type="secondary">{timeZoneLabel(t, format, timeZone)}</Text></Space>
        {timeZone !== browser && <div className="cp-tz-note">{t('cloud.prewarm.willChange', { timeZone: timeZoneLabel(t, format, browser) })}</div>}</div>
      {stored !== null && nextText !== null && cloud.lifecycle !== 'dormant' && cloud.lifecycle !== 'frozen' && <Text type="secondary">{t('cloud.prewarm.next', { when: nextText })}</Text>}
      <Space>
        <Button type="primary" loading={saving === 'save'} disabled={days.length === 0 || !/^([01]\d|2[0-3]):[0-5]\d$/u.test(at)}
          onClick={() => void save({ days: [...days].sort((a, b) => a - b), at, timeZone: browser })}>{t('common.save')}</Button>
        {stored !== null && <Button loading={saving === 'off'} onClick={() => void save(null)}>{t('cloud.prewarm.turnOff')}</Button>}
      </Space>
    </Space>
    {error !== null && <Alert className="cp-inline-error" type="error" showIcon message={error} />}
  </Card>;
}
